import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, POSTS } from "@/lib/blog";
import { FadeUp, MaskedLine, GoldDot } from "@/components/Reveal";
import Booking from "@/components/Booking";
import Footer from "@/components/Footer";

export default function BlogPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Journal — Royal Essence Massage Edmonton";
    return () => {
      document.title = "Royal Essence Massage — Massage Beyond Muscles";
    };
  }, []);

  return (
    <div className="bg-ivory font-sans text-walnut" data-testid="blog-page">
      <main>
        <section className="bg-forest pb-24 pt-40 text-ivory lg:pb-28 lg:pt-48">
          <div className="mx-auto max-w-[1500px] px-6 lg:px-14">
            <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl" data-testid="blog-headline">
              <MaskedLine delay={0.15}>The Journal</MaskedLine>
              <MaskedLine delay={0.3}>
                <span className="italic text-sand">on bodies &amp; care</span>
                <span className="mb-2 ml-3 inline-block h-2 w-2 rounded-full bg-gold align-baseline" />
              </MaskedLine>
            </h1>
            <FadeUp delay={0.45}>
              <p className="mt-10 max-w-xl font-sans text-base font-light leading-relaxed tracking-wide text-ivory/75" data-testid="blog-subcopy">
                Honest, unhurried writing about massage: what helps, what to
                expect, and when something is a question for a doctor instead.
              </p>
            </FadeUp>
          </div>
        </section>

        {CATEGORIES.map((cat, ci) => (
          <section
            key={cat}
            data-chapter={`B${ci + 1}`}
            className={`py-24 lg:py-32 ${ci % 2 ? "bg-sand/40" : ""}`}
            data-testid={`blog-category-${ci}`}
          >
            <div className="mx-auto max-w-[1500px] px-6 lg:px-14">
              <FadeUp className="mb-14 flex items-center gap-4">
                <GoldDot />
                <span className="font-sans text-[11px] font-medium uppercase tracking-[0.4em] text-gold">
                  {String(ci + 1).padStart(2, "0")} — {cat}
                </span>
              </FadeUp>
              <div className="border-t border-walnut/30">
                {POSTS.filter((p) => p.category === cat).map((post, i) => (
                  <FadeUp key={post.slug} delay={Math.min(i * 0.06, 0.3)}>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="group grid items-center gap-4 border-b border-walnut/30 py-10 transition-colors duration-700 hover:bg-sand/30 lg:grid-cols-12 lg:gap-8 lg:px-6"
                      data-testid={`blog-row-${post.slug}`}
                    >
                      <span className="flex items-center gap-4 lg:col-span-1">
                        <span className="h-[5px] w-[5px] rounded-full bg-gold opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                        <span className="font-serif text-lg font-light italic text-walnut/65">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </span>
                      <span className="lg:col-span-5">
                        <span className="font-serif text-2xl font-light leading-snug tracking-wide text-forest transition-colors duration-500 group-hover:text-walnut lg:text-3xl">
                          {post.title}
                        </span>
                        <span className="mt-3 block font-sans text-[10px] font-medium uppercase tracking-[0.3em] text-walnut/65">
                          {post.read}
                        </span>
                      </span>
                      <span className="block font-sans text-sm font-light leading-relaxed tracking-wide text-walnut/85 lg:col-span-5">
                        {post.excerpt}
                      </span>
                      <span className="flex lg:col-span-1 lg:justify-end">
                        <span className="h-px w-8 rounded-full bg-gold/80 transition-all duration-700 group-hover:w-14 group-hover:bg-gold" aria-hidden="true" />
                      </span>
                    </Link>
                  </FadeUp>
                ))}
              </div>
            </div>
          </section>
        ))}

        <Booking />
      </main>
      <Footer />
    </div>
  );
}
